import React, { useState } from 'react'
import { FileData } from '@/types'
import { FileInput } from '@/components/ui/FileInput'
import { Button } from '@/components/ui/Button'
import { toastError, toastSuccess } from '@/utils/toast'
import { Download } from 'phosphor-react'

interface RegimeFileSectionProps {
  currentRegime?: FileData
  onUpload: (file: File) => Promise<void>
  onDownload: () => Promise<void>
  isUploading: boolean
  readOnly?: boolean
}

export const RegimeFileSection: React.FC<RegimeFileSectionProps> = ({
  currentRegime,
  onUpload,
  onDownload,
  isUploading,
  readOnly = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const handleFileChange = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setSelectedFile(file)
  }

  const handleFileRemove = () => {
    setSelectedFile(null)
  }

  const handleSubmit = async () => {
    if (!selectedFile) return

    try {
      await onUpload(selectedFile)
      toastSuccess('Fichier régime téléchargé avec succès')
      setSelectedFile(null)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erreur lors du téléchargement du fichier régime'
      toastError(errorMessage)
    }
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      await onDownload()
      toastSuccess('Fichier régime téléchargé avec succès')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erreur lors du téléchargement du fichier régime'
      toastError(errorMessage)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 bg-[var(--color-gray-100)] rounded-lg">
          <p className="text-sm text-[var(--color-text-primary)]">
            {currentRegime?.filename ? (
              <>
                Fichier actuel: <span className="font-medium">{currentRegime.filename}</span>
              </>
            ) : (
              <span className="text-[var(--color-text-muted)]">
                Aucun fichier régime disponible
              </span>
            )}
          </p>
          <Button
            onClick={handleDownload}
            isLoading={isDownloading}
            disabled={isDownloading || isUploading || !currentRegime?.filename}
            variant="outline"
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" weight="regular" />
            Télécharger
          </Button>
        </div>
        {!readOnly && (
          <>
            <FileInput
              label="Télécharger un nouveau fichier régime"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading || isDownloading}
              selectedFiles={selectedFile ? [selectedFile] : []}
              onFileRemove={handleFileRemove}
            />
            <div className="flex justify-end">
              <Button onClick={handleSubmit} isLoading={isUploading} disabled={!selectedFile}>
                Enregistrer le fichier régime
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
